import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

export const PayrollContext = createContext();

export const usePayroll = () => {
  const context = useContext(PayrollContext);
  if (!context) {
    throw new Error('usePayroll must be used within a PayrollProvider');
  }
  return context;
};

export const PayrollProvider = ({ children }) => {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [payrolls, setPayrolls] = useState([]);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    filteredCount: 0,
    totalPayrollAmount: 0,
    totalPaidAmount: 0,
    totalPendingAmount: 0,
    totalPaidCount: 0,
    totalApprovedCount: 0,
    totalPendingCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedEmployeePayroll, setSelectedEmployeePayroll] = useState(null);

  // Fetch monthly payroll list with stats and filters
  const fetchMonthlyPayroll = useCallback(async (month = selectedMonth, year = selectedYear, search = '', status = 'all', siteId = 'all') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (month) params.append('month', month);
      if (year) params.append('year', year);
      if (search) params.append('search', search);
      if (status && status !== 'all') params.append('status', status);
      if (siteId && siteId !== 'all') params.append('siteId', siteId);

      const response = await api.get(`/payroll/summary?${params.toString()}`);
      if (response.data && response.data.success) {
        setPayrolls(response.data.data || []);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
        return { success: true, data: response.data.data, stats: response.data.stats };
      }
      return { success: false };
    } catch (error) {
      console.error('Error fetching payroll summary:', error);
      const message = error.response?.data?.message || 'Failed to fetch payroll records';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Fetch single employee detailed payroll & attendance breakdown
  const fetchEmployeePayroll = useCallback(async (employeeId, month = selectedMonth, year = selectedYear) => {
    try {
      setLoading(true);
      const response = await api.get(`/payroll/employee/${employeeId}?month=${month}&year=${year}`);
      if (response.data && response.data.success) {
        setSelectedEmployeePayroll(response.data.data);
        return { success: true, data: response.data.data };
      }
      return { success: false };
    } catch (error) {
      console.error('Error fetching employee payroll details:', error);
      const message = error.response?.data?.message || 'Failed to fetch employee details';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Save / Update payroll record with adjustments (bonus, deduction, advance, notes, status)
  const savePayrollAdjustment = useCallback(async (payrollData) => {
    try {
      setLoading(true);
      const { siteId, ...savePayload } = payrollData || {};
      const response = await api.post('/payroll/save', savePayload);
      if (response.data && response.data.success) {
        toast.success('Payroll adjustment saved successfully');
        await fetchMonthlyPayroll(
          savePayload.month || selectedMonth,
          savePayload.year || selectedYear,
          '',
          'all',
          siteId || 'all'
        );
        return { success: true, data: response.data.data };
      }
      return { success: false };
    } catch (error) {
      console.error('Error saving payroll adjustment:', error);
      const message = error.response?.data?.message || 'Failed to save payroll adjustment';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchMonthlyPayroll, selectedMonth, selectedYear]);

  // Update payroll payment status (Pending -> Approved -> Paid)
  const updatePaymentStatus = useCallback(async (arg1, arg2) => {
    try {
      setLoading(true);
      let response;
      let requestSiteId = 'all';
      if (typeof arg1 === 'object' && arg1 !== null) {
        // Called as updatePaymentStatus({ employeeId, month, year, status, ... })
        const { siteId, ...savePayload } = arg1;
        requestSiteId = siteId || 'all';
        response = await api.post('/payroll/save', savePayload);
      } else {
        // Called as updatePaymentStatus(payrollId, statusData)
        const { siteId, ...statusPayload } = arg2 || {};
        requestSiteId = siteId || 'all';
        response = await api.put(`/payroll/${arg1}/status`, statusPayload);
      }
      if (response.data && response.data.success) {
        const newStatus = arg2?.status || arg1?.status || 'updated';
        toast.success(`Payment marked as ${newStatus}`);
        await fetchMonthlyPayroll(
          arg1?.month || selectedMonth,
          arg1?.year || selectedYear,
          '',
          'all',
          requestSiteId
        );
        return { success: true, data: response.data.data };
      }
      return { success: false };
    } catch (error) {
      console.error('Error updating payroll status:', error);
      const message = error.response?.data?.message || 'Failed to update payment status';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchMonthlyPayroll, selectedMonth, selectedYear]);

  // Bulk generate / sync payroll for all employees for a month
  const bulkGeneratePayroll = useCallback(async (month = selectedMonth, year = selectedYear, siteId = 'all') => {
    try {
      setLoading(true);
      const payload = { month, year };
      if (siteId && siteId !== 'all') payload.siteId = siteId;
      const response = await api.post('/payroll/bulk-generate', payload);
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Payroll synced successfully');
        await fetchMonthlyPayroll(month, year, '', 'all', siteId);
        return { success: true, stats: response.data.stats };
      }
      return { success: false };
    } catch (error) {
      console.error('Error bulk generating payroll:', error);
      const message = error.response?.data?.message || 'Failed to bulk generate payroll';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchMonthlyPayroll, selectedMonth, selectedYear]);

  // Bulk update payroll status (Bulk Approve, Bulk Paid)
  const bulkUpdatePayrollStatus = useCallback(async ({ employeeIds, month = selectedMonth, year = selectedYear, status, paymentDate, paymentMethod, paymentNotes, siteId = 'all' }) => {
    try {
      setLoading(true);
      const response = await api.post('/payroll/bulk-status', {
        employeeIds,
        month,
        year,
        status,
        paymentDate,
        paymentMethod,
        paymentNotes
      });
      if (response.data && response.data.success) {
        toast.success(response.data.message || `${employeeIds.length} records marked as ${status}`);
        await fetchMonthlyPayroll(month, year, '', 'all', siteId);
        return { success: true, ...response.data };
      }
      return { success: false };
    } catch (error) {
      console.error('Error in bulk payroll status update:', error);
      const message = error.response?.data?.message || 'Failed to update payroll status';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchMonthlyPayroll, selectedMonth, selectedYear]);

  const value = {
    payrolls,
    payrollData: payrolls,
    stats,
    summaryStats: stats,
    loading,
    selectedMonth,
    selectedYear,
    setSelectedMonth,
    setSelectedYear,
    selectedEmployeePayroll,
    setSelectedEmployeePayroll,
    fetchMonthlyPayroll,
    fetchEmployeePayroll,
    savePayrollAdjustment,
    updatePaymentStatus,
    bulkGeneratePayroll,
    bulkUpdatePayrollStatus
  };

  return (
    <PayrollContext.Provider value={value}>
      {children}
    </PayrollContext.Provider>
  );
};

export default PayrollContext;
